/**
 * Recording Management Types
 * 
 * Part of Native Voice Capture feature (Epic 13)
 * Task 2.10: Web Recording Management UI
 */

export interface Recording {
  id: string;
  title?: string;
  duration: number; // seconds
  size: number; // bytes
  format: string;
  sampleRate: number;
  recordedAt: string; // ISO 8601
  audioUrl?: string;
  transcriptId?: string;
  transcriptUrl?: string;
  transcriptText?: string;
  status: 'uploaded' | 'processing' | 'transcribed' | 'failed';
  sourceType: 'web-recording' | 'ios-recording' | 'android-recording';
  metadata?: RecordingMetadata;
}

export interface RecordingMetadata {
  quality?: 'low' | 'medium' | 'high';
  location?: string;
  tags?: string[];
  notes?: string;
}

export interface RecordingListProps {
  recordings?: Recording[];
  loading?: boolean;
  error?: string | null;
  onRecordingSelect?: (recording: Recording) => void;
  onDelete?: (id: string) => void;
  onDownload?: (id: string, type: 'audio' | 'transcript') => void;
  viewMode?: 'grid' | 'list';
  sortBy?: 'date' | 'duration' | 'title';
  sortOrder?: 'asc' | 'desc';
  filterStatus?: Recording['status'] | 'all';
  searchQuery?: string;
}

export interface RecordingCardProps {
  recording: Recording;
  onSelect?: () => void;
  onDelete?: () => void;
  onDownload?: (type: 'audio' | 'transcript') => void;
  selected?: boolean;
  viewMode?: 'grid' | 'list';
}

export interface RecordingDetailsProps {
  recording: Recording;
  onClose: () => void;
  onDelete?: () => void;
  onDownload?: (type: 'audio' | 'transcript') => void;
  onUpdate?: (id: string, updates: Partial<Recording>) => void;
}

export interface AudioPlayerProps {
  src: string;
  title?: string;
  onEnded?: () => void;
  onError?: (error: Error) => void;
  className?: string;
}

// Filters and sorting
export type SortField = 'date' | 'duration' | 'title' | 'size';
export type SortOrder = 'asc' | 'desc';
export type StatusFilter = Recording['status'] | 'all';
export type ViewMode = 'grid' | 'list';

export interface RecordingFilters {
  status: StatusFilter;
  sortBy: SortField;
  sortOrder: SortOrder;
  searchQuery: string;
  dateRange?: {
    start?: Date;
    end?: Date;
  };
}

// Utility functions
export const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    // Today - show time
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'long' });
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  }
};

export const getStatusColor = (status: Recording['status']): string => {
  switch (status) {
    case 'uploaded':
      return 'blue';
    case 'processing':
      return 'yellow';
    case 'transcribed':
      return 'green';
    case 'failed':
      return 'red';
    default:
      return 'gray';
  }
};

export const getStatusLabel = (status: Recording['status']): string => {
  switch (status) {
    case 'uploaded':
      return 'Uploaded';
    case 'processing':
      return 'Processing...';
    case 'transcribed':
      return 'Ready';
    case 'failed':
      return 'Failed';
    default:
      return 'Unknown';
  }
};

export const getSourceTypeLabel = (sourceType: Recording['sourceType']): string => {
  switch (sourceType) {
    case 'web-recording':
      return 'Web';
    case 'ios-recording':
      return 'iOS';
    case 'android-recording':
      return 'Android';
    default:
      return 'Unknown';
  }
};

